import { zodResolver } from "@hookform/resolvers/zod";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { t } from "ttag";
import z from "zod";

import { useOnboardingUser } from "@/features/onboarding/hooks";
import {
  checkoutSchema,
  contactInfoSchema,
  onboardingUserSchema,
} from "@/features/onboarding/models";
import {
  PAYMENT_METHOD_TYPE,
  PAYMENT_REDIRECT_METHOD_KEY,
  PaymentMethodType,
} from "@/shared/domains";

const baseFormSchema = contactInfoSchema
  .omit({
    firstName: true,
    lastName: true,
    email: true,
    phoneNumber: true,
    smsMarketingConsent: true,
  })
  .partial({ billing: true })
  .extend({
    isBillingSameAsShipping: z.boolean().optional(),
    state: onboardingUserSchema.shape.state,
  })
  .refine((data) => {
    if (!data.isBillingSameAsShipping) {
      return data.billing?.address && data.billing.city && data.billing.zipCode;
    }
    return true;
  })
  .and(checkoutSchema);

const formSchema = baseFormSchema.and(
  z.object({
    termsAndConditionsConsent: z.literal(true, {
      error: () => t`You must agree to the terms and conditions`,
    }),
  }),
);

const formSchemaWithoutTermsAndConditions = baseFormSchema.and(
  z.object({ termsAndConditionsConsent: z.boolean().optional() }),
);

// Types the field as optional even though the "required" schema enforces
// `literal(true)` at runtime for Longevity/Questionnaire checkout. Honestly
// reflecting "may be absent" here (rather than lying that it's always `true`)
// is what lets Funnel Payment and the purchase form skip the field entirely
// without misrepresenting its type.
export type ContactInfoFormValues = z.infer<
  typeof formSchemaWithoutTermsAndConditions
>;

export const useCreateUserForm = ({
  requireTermsAndConditionsConsent = true,
}: { requireTermsAndConditionsConsent?: boolean } = {}) => {
  const [searchParams] = useSearchParams();

  const user = useOnboardingUser();

  const paymentMethod = PaymentMethodType.safeParse(
    searchParams.get(PAYMENT_REDIRECT_METHOD_KEY),
  ).data;

  return useForm<ContactInfoFormValues>({
    // Both schemas infer to shapes assignable to ContactInfoFormValues (the
    // field is optional there either way); the cast only works around
    // TypeScript's inability to unify a ternary of two zodResolver() calls
    // into one Resolver<T>, not a real strictness mismatch.
    resolver: (requireTermsAndConditionsConsent
      ? zodResolver(formSchema)
      : zodResolver(
          formSchemaWithoutTermsAndConditions,
        )) as Resolver<ContactInfoFormValues>,
    mode: "onTouched",
    defaultValues: {
      isBillingSameAsShipping: true,
      paymentMethodType: paymentMethod ?? PAYMENT_METHOD_TYPE.CARD,
      shipping: user?.shipping,
      state: user?.state,
    },
  });
};
